(ns practitest.frontend.components2.dashboard.charts.render-helpers)

(defn update-svg-scale!
  "Set --svg-scale CSS custom property: screen pixels per viewBox unit.

   Problem: pie labels live inside <foreignObject>. The browser renders
   foreignObject content in viewBox coordinates, then scales the whole
   SVG to fit its container. So `font-size: 12px` does not mean 12 screen
   pixels — it means 12 viewBox units, which grow or shrink with the SVG.

   Solution: --svg-scale = clientWidth (actual pixels on screen)
                         / viewBoxWidth (internal SVG coordinate space).
   This is how many screen pixels one viewBox unit maps to. The CSS uses
   font-size: calc(12px / var(--svg-scale, 1)) to divide out the scaling,
   keeping labels at a constant 12px on screen regardless of SVG size.

   Example: viewBox width=400, rendered at 800px → --svg-scale=2
   → CSS sets font-size=6px → browser scales 6*2 = 12px on screen."
  [svg-element]
  (let [vb-w (some-> svg-element .-viewBox .-baseVal .-width)]
    (when (and vb-w (pos? vb-w))
      (.setProperty (.-style svg-element) "--svg-scale"
                    (str (/ (.-clientWidth svg-element) vb-w))))))

(defn- computed-css-text
  "Build a CSS declaration string from an element's computed style."
  [el]
  (let [computed (js/getComputedStyle el)
        len      (.-length computed)]
    (loop [i 0 acc ""]
      (if (>= i len)
        acc
        (let [prop (.item computed i)]
          (recur (inc i)
                 (str acc prop ": " (.getPropertyValue computed prop) "; ")))))))

(defn embed-styles!
  "Read computed CSS for selectors from an in-DOM SVG and embed them
   as a <style> element in a target SVG (typically a detached clone)."
  [source-svg target-svg selectors]
  (let [css (reduce
             (fn [acc selector]
               (if-let [el (.querySelector source-svg selector)]
                 (str acc selector " { " (computed-css-text el) " }\n")
                 acc))
             "" selectors)
        style-el (.createElementNS js/document "http://www.w3.org/2000/svg" "style")]
    (set! (.-textContent style-el) css)
    (.prepend target-svg style-el)))

(defn ensure-group!
  "Reuse existing <g> by class name, or append a new one."
  ([parent class-name]
   (let [existing (.select parent (str "g." class-name))]
     (if (.empty existing)
       (-> parent (.append "g") (.attr "class" class-name))
       existing)))
  ([parent class-name transform]
   (let [existing (.select parent (str "g." class-name))]
     (if (.empty existing)
       (-> parent (.append "g") (.attr "class" class-name) (.attr "transform" transform))
       existing))))

(defn clone-svg
  "Clone an in-DOM SVG element with explicit width/height attributes
   set from its bounding rect. Returns a detached clone ready for
   serialization."
  [svg-element]
  (let [clone    (.cloneNode svg-element true)
        svg-rect (.getBoundingClientRect svg-element)]
    (.setAttribute clone "width" (.-width svg-rect))
    (.setAttribute clone "height" (.-height svg-rect))
    clone))
